<template>
  <div v-if="available" class="form form-sm mx-auto">
    <h2 class="mb-4">Password</h2>

    <template v-if="stage === 'idle'">
      <!-- Entra can't verify an "old" password outside this same
           code-and-new-password flow, so there is no current-password field
           here - unlike the legacy local-credential form this replaces. -->
      <p class="small lh-160 mb-3">
        Your password is managed by your organization's sign-in provider.
      </p>
      <b-button size="sm" variant="primary" :disabled="busy" @click="start">
        {{ busy ? "Sending…" : "Change password" }}
      </b-button>
    </template>

    <template v-else-if="stage === 'done'">
      <b-alert variant="primary" show class="text-center mb-0">
        Your password has been changed.
      </b-alert>
    </template>

    <template v-else>
      <small class="mt-1 lh-160 d-block mb-3">
        <template v-if="target">
          We sent a verification code to {{ target }}. Enter it below with
          your new password to confirm the change.
        </template>
        <template v-else>
          Enter the verification code we emailed you, along with your new
          password, to confirm the change.
        </template>
      </small>

      <!-- No maxlength/exact-length rule: this is Entra's native-auth email
           code (8 digits per entra_sso.native_views._FAKE_CODE_LENGTH and its
           own tests), NOT the unrelated 6-digit SMS code from
           phone_verification.py that Form2FASettingsSso's code box sends -
           those are two different code systems and must not share a length
           assumption. Same base-input, rules and label as SsoSetPassword.vue's
           own code field (the first-time equivalent of this screen). -->
      <b-form-input
        v-model="codeInput"
        class="mb-2"
        inputmode="numeric"
        autocomplete="one-time-code"
        placeholder="Verification code"
        :disabled="busy"
        @keyup.enter="confirmChange"
      ></b-form-input>

      <!-- Same component/props pattern as SsoSetPassword's first-time flow,
           so the two password-entry experiences look and behave identically. -->
      <BasePasswordInput
        v-model="passwordInput"
        :status="passwordStatus"
        maxlength="64"
        valid-title="Meets the password requirements"
        invalid-title="Doesn't meet the password requirements yet"
        name="New password"
        label="New password"
        class="mt-3"
      />
      <PasswordCriteriaChecklist
        :password="passwordInput"
        @validity="(isValid) => (passwordAcceptable = isValid)"
      />
      <BasePasswordInput
        v-model="confirmInput"
        :status="confirmStatus"
        maxlength="64"
        valid-title="Passwords match"
        invalid-title="Passwords don't match"
        name="Confirm password"
        label="Confirm password"
      />

      <b-button
        size="sm"
        variant="primary"
        class="mt-2"
        :disabled="!canSubmit"
        @click="confirmChange"
      >
        {{ busy ? "Changing…" : "Change password" }}
      </b-button>
      <b-button
        size="sm"
        variant="link"
        class="text-muted"
        :disabled="busy"
        @click="start"
        >Resend code</b-button
      >
      <b-button
        size="sm"
        variant="link"
        class="text-muted"
        :disabled="busy"
        @click="cancel"
        >Cancel</b-button
      >
    </template>
  </div>
</template>

<script>
import Vue from 'vue'
import { defineComponent, ref, computed, onMounted } from '@vue/composition-api'

import { AuthenticationService } from '@services'
import PasswordCriteriaChecklist from '@components/PasswordCriteriaChecklist'
import BasePasswordInput from '@components/base/base-password-input/BasePasswordInput'

// Every DRF response is wrapped by api.utils.APIRenderer as {status, data},
// which is why the rest of the app reads response.data.data. Tolerate both
// shapes rather than assuming - same reasoning and shape as
// Form2FASettingsSso.vue's own unwrap(): reading one level short silently
// yields undefined, which here would mean the form looks like it did nothing.
const unwrapBody = (body) => {
  const wrapped = body && typeof body === 'object' && 'status' in body && 'data' in body
  return (wrapped ? body.data : body) || {}
}
const unwrap = (res) => unwrapBody((res && res.data) || {})
// Returns the whole unwrapped error body (not just .detail) - confirmChange
// needs .code too, to tell a flow_expired failure apart from any other.
const errorDetail = (err) => unwrapBody((err && err.response && err.response.data) || {})

export default defineComponent({
  name: 'FormChangePasswordSso',
  components: { PasswordCriteriaChecklist, BasePasswordInput },
  setup(props, { emit }) {
    // available stays false until the server confirms this user's password
    // really is managed in Entra - same reasoning as Form2FASettingsSso: show
    // nothing rather than controls that do nothing for a legacy-auth user.
    const available = ref(false)
    // 'idle' -> nothing requested yet, 'active' -> code/password form shown,
    // 'done' -> just finished. A single stage instead of two booleans so the
    // template never has to reason about a combination that shouldn't exist.
    const stage = ref('idle')
    const busy = ref(false)
    const flow = ref('')
    const target = ref('')
    const codeInput = ref('')
    const passwordInput = ref('')
    const confirmInput = ref('')
    // Driven by PasswordCriteriaChecklist - gates submit so a password Entra
    // would reject doesn't cost a round trip.
    const passwordAcceptable = ref(false)

    const passwordStatus = computed(() => {
      if (!passwordInput.value) return null
      return passwordAcceptable.value ? 'valid' : 'invalid'
    })
    const passwordsMatch = computed(
      () => !!passwordInput.value && passwordInput.value === confirmInput.value
    )
    const confirmStatus = computed(() => {
      if (!confirmInput.value) return null
      return passwordsMatch.value ? 'valid' : 'invalid'
    })
    // Single source of truth for "is this submittable", used by both the
    // button's :disabled and confirmChange's own guard (the latter needed
    // because @keyup.enter on the code input calls confirmChange directly,
    // bypassing the button).
    const canSubmit = computed(
      () => !busy.value && !!codeInput.value && passwordAcceptable.value && passwordsMatch.value
    )

    // Free GET, no side effects - safe to call on every mount. Opening
    // Settings must never itself email a code; that only happens once the
    // user explicitly clicks "Change password" below (see start()).
    const load = async () => {
      const res = await AuthenticationService.ssoNativeSelfSsprAvailability().catch(
        () => null
      )
      const data = unwrap(res)
      available.value = data.available === true
      // Tells the page nothing right now - there is no legacy panel left for
      // it to hand control to - but mirrors Form2FASettingsSso's own emit so
      // the two panels stay consistent if that ever changes again.
      emit('availability', available.value)
    }

    const resetFields = () => {
      codeInput.value = ''
      passwordInput.value = ''
      confirmInput.value = ''
      passwordAcceptable.value = false
    }

    // Request a code (also used for "Resend"). The server replaces any
    // pending code, so a resend and this initial request are the same call.
    const start = async () => {
      if (busy.value) return
      busy.value = true
      const res = await AuthenticationService.ssoNativeSelfSsprStart().catch(
        (err) => {
          Vue.prototype.$toasted.show(
            errorDetail(err).detail || "We couldn't send a code. Please try again.",
            { type: 'error' }
          )
          return null
        }
      )
      busy.value = false
      if (!res) return
      const data = unwrap(res)
      // A 200 with no flow means resolve() answered {available:false,...}
      // instead of actually starting - e.g. the account was unlinked in the
      // moment between the page loading and this click. Treat it as a
      // failure rather than silently advancing to a code box for a code that
      // was never sent.
      if (!data.flow) {
        available.value = false
        emit('availability', false)
        Vue.prototype.$toasted.show(
          "This isn't available right now. Please try again.",
          { type: 'error' }
        )
        return
      }
      flow.value = data.flow
      target.value = data.target || ''
      resetFields()
      stage.value = 'active'
      Vue.prototype.$toasted.show('We sent you a verification code.')
    }

    const cancel = () => {
      stage.value = 'idle'
      resetFields()
    }

    const confirmChange = async () => {
      if (!canSubmit.value) {
        return
      }
      busy.value = true
      const res = await AuthenticationService.ssoNativeSelfSsprVerify(
        flow.value,
        codeInput.value,
        passwordInput.value
      ).catch((err) => {
        busy.value = false
        const detail = errorDetail(err)
        Vue.prototype.$toasted.show(detail.detail || "That didn't work. Please try again.", {
          type: 'error'
        })
        // Flow expired -> get a fresh code rather than leave a dead form up.
        // busy is already cleared above, so start()'s own guard doesn't block
        // this call.
        if (detail.code === 'flow_expired') start()
        return null
      })
      if (!res) return
      busy.value = false
      const data = unwrap(res)
      // Only a real {"status": "password_set"} counts as success - a 200
      // with anything else (e.g. {available:false,...}, if the account was
      // unlinked mid-flow) must not be shown as "changed" when it wasn't.
      if (data.status !== 'password_set') {
        Vue.prototype.$toasted.show(
          "This isn't available right now. Please try again.",
          { type: 'error' }
        )
        return
      }
      stage.value = 'done'
      resetFields()
      Vue.prototype.$toasted.show('Your password has been changed.')
    }

    onMounted(load)

    return {
      available,
      stage,
      busy,
      target,
      codeInput,
      passwordInput,
      confirmInput,
      passwordAcceptable,
      passwordStatus,
      passwordsMatch,
      confirmStatus,
      canSubmit,
      start,
      cancel,
      confirmChange
    }
  }
})
</script>
